import { Location } from 'history';
import React from 'react';
import { connect, MapStateToProps } from 'react-redux';
import { match, Redirect, Route, RouteComponentProps, Switch } from 'react-router';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { selectCatalogingOnSiteEnabled } from '~src/catalog/selectors/CatalogSelectors';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { PublishedConnectionSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';

import { SessionSiteSelectors } from '../../bootstrap/redux/selectors/SessionInfoSelectors';
import { AppState } from '../../bootstrap/redux/state/AppState';
import { RouterError } from '../../bootstrap/routerHelpers/RouterError';
import { PublishedConnectionSpaceProvider } from './PublishedConnectionSpaceProvider';

export interface PublishedConnectionRouteParams {
  readonly id: string;
}

export interface PublishedConnectionRouteComponentProps extends RouteComponentProps<PublishedConnectionRouteParams> {
  readonly match: match<PublishedConnectionRouteParams>;
  readonly location: Location;
}

export interface PublishedConnectionSpaceRouterOwnProps {
  readonly siteName: string;
  readonly previousUrl: string;
  readonly urls: SpacesSiteUrls;
}

export interface PublishedConnectionSpaceRouterStateProps {
  readonly isRequestAccessEnabled: boolean;
  readonly catalogingOnSiteEnabled: boolean;
}

export type PublishedConnectionSpaceRouterProps =
  PublishedConnectionRouteComponentProps &
  PublishedConnectionSpaceRouterOwnProps &
  PublishedConnectionSpaceRouterStateProps;

export class PublishedConnectionSpaceRouterComponent extends React.Component<PublishedConnectionSpaceRouterProps, {}> {
  public render() {
    return (
      <Switch>
        <Route exact path={this.props.urls.virtualConnectionPath(':id')} render={(routeProps: PublishedConnectionRouteComponentProps) =>
          <Redirect to={this.props.urls.virtualConnectionOverviewPath(routeProps.match.params.id)} />
        } />
        <Route exact path={this.props.urls.virtualConnectionOverviewPath(':id')} render={this.spaceProviderFor('tables')} />
        <Route exact path={this.props.urls.virtualConnectionScheduledTasksPath(':id')} render={this.spaceProviderFor('scheduledTasks')} />
        <Route exact path={this.props.urls.virtualConnectionRunHistoryPath(':id')} render={this.spaceProviderFor('runHistory')}/>
        <Route path={this.props.urls.virtualConnectionGrantAccessPath(':id')} render={(routeProps: PublishedConnectionRouteComponentProps) => (
          <Redirect to={this.props.urls.virtualConnectionOverviewPath(routeProps.match.params.id)} />
        )} />
        {this.getLineageRoute()}
        <Route render={() => <RouterError errorType={'default'} urls={this.props.urls} />} />
      </Switch>
    );
  }

  private getLineageRoute = (): JSX.Element => {
    if (FeatureFlags.publishedConnectionsLineageEnabled && this.props.catalogingOnSiteEnabled) {
      return <Route path={this.props.urls.virtualConnectionLineagePath(':id')} render={this.spaceProviderFor('lineage')} />;
    } else {
      // we want to match on any virtual connection path starting with /lineage so don't use 'exact' on the route
      return (
        <Route path={this.props.urls.virtualConnectionLineagePath(':id')} render={({ match }) => (
          <Redirect to={this.props.urls.virtualConnectionOverviewPath(match.params.id)} />
        )} />);
    }
  };

  private spaceProviderFor = (subplace: PublishedConnectionSubspaceName): (routeProps: PublishedConnectionRouteComponentProps) => JSX.Element => {
    return (routeProps: PublishedConnectionRouteComponentProps) => {
      const grantAccessDisplay = this.props.previousUrl.includes(this.props.urls.virtualConnectionGrantAccessPath(routeProps.match.params.id));
      return (
        <PublishedConnectionSpaceProvider
          siteName={this.props.siteName}
          publishedConnectionId={routeProps.match.params.id}
          siteUrls={this.props.urls}
          subplaceName={subplace}
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
          isRequestAccessEnabled={this.props.isRequestAccessEnabled}
          grantAccessDisplay={grantAccessDisplay}
          grantAccessUrl={grantAccessDisplay ? this.props.previousUrl : ''}
        />
      );
    };
  };
}

const mapStateToProps: MapStateToProps<PublishedConnectionSpaceRouterStateProps, PublishedConnectionSpaceRouterOwnProps, {}> = (state: AppState) => ({
  isRequestAccessEnabled: SessionSiteSelectors.isRequestAccessEnabled(state),
  catalogingOnSiteEnabled: selectCatalogingOnSiteEnabled(state)
});

export const PublishedConnectionSpaceRouter: React.ComponentClass<PublishedConnectionSpaceRouterOwnProps>
  = connect(mapStateToProps, null, null, { pure: false })(PublishedConnectionSpaceRouterComponent);
